import React, { useRef, useState } from 'react';
import { Space, Input, Select, Button, AutoComplete } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import {
    SUGGEST_COMMON_CONFIG_KEYS,
    SuggestCommonConfigKeysResponse,
    SuggestCommonConfigKeysVariables
} from '../graphql/commonConfigQueries';

const { Option } = Select;

interface CommonConfigFilterProps {
    // 筛选相关
    searchKeyword: string;
    filterDescription: string;
    filterConfigType?: string;
    filterIsActive?: boolean;
    onSearchKeywordChange: (value: string) => void;
    onFilterDescriptionChange: (value: string) => void;
    onFilterConfigTypeChange: (value: string | undefined) => void;
    onFilterIsActiveChange: (value: boolean | undefined) => void;
    onFilter: () => void;
    onReset: () => void;

    // 操作按钮相关
    selectedCount: number;
    deleteLoading: boolean;
    statusLoading: boolean;
    onCreateClick: () => void;
    onBatchDelete: () => void;
    onBatchChangeStatus: () => void;
    onDebugConfig: () => void;
}

const CommonConfigFilter: React.FC<CommonConfigFilterProps> = ({
    searchKeyword,
    filterDescription,
    filterConfigType,
    filterIsActive,
    onSearchKeywordChange,
    onFilterDescriptionChange,
    onFilterConfigTypeChange,
    onFilterIsActiveChange,
    onFilter,
    onReset,
    selectedCount,
    deleteLoading,
    statusLoading,
    onCreateClick,
    onBatchDelete,
    onBatchChangeStatus,
    onDebugConfig
}) => {
    const { t } = useTranslation();
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();

    // configKey 前缀搜索建议：debounce 200ms 调用 suggestCommonConfigKeys
    const [suggestOptions, setSuggestOptions] = useState<{ value: string }[]>([]);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [fetchSuggest] = useLazyQuery<SuggestCommonConfigKeysResponse, SuggestCommonConfigKeysVariables>(
        SUGGEST_COMMON_CONFIG_KEYS,
        {
            fetchPolicy: 'no-cache',
            onCompleted: (resp) => {
                setSuggestOptions((resp.suggestCommonConfigKeys ?? []).map((v) => ({ value: v })));
            }
        }
    );
    const handleConfigKeySearch = (raw: string) => {
        onSearchKeywordChange(raw);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const prefix = raw.trim();
        if (!prefix) {
            setSuggestOptions([]);
            return;
        }
        debounceTimer.current = setTimeout(() => {
            fetchSuggest({ variables: { prefix, limit: 20 } });
        }, 200);
    };

    const labelConfigKey = String(t('commonConfig.filter.configKeyLabel'));
    const labelDescription = String(t('commonConfig.filter.descriptionLabel'));
    const labelConfigType = String(t('commonConfig.filter.configTypeLabel'));
    const labelStatus = String(t('commonConfig.filter.statusLabel'));

    const col1Labels = [labelConfigKey, labelStatus];
    const col2Labels = [labelDescription];
    const col3Labels = [labelConfigType];

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(col1Labels)}>{labelConfigKey}</span>
                        <div className={inputContainerClassName}>
                            <AutoComplete
                                className="w-full"
                                value={searchKeyword}
                                options={suggestOptions}
                                onSearch={handleConfigKeySearch}
                                onChange={(v) => onSearchKeywordChange(v ?? '')}
                                onSelect={(v) => onSearchKeywordChange(v)}
                                placeholder={String(t('commonConfig.filter.searchConfigKey'))}
                                allowClear
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelDescription}
                        value={filterDescription}
                        onChange={(e) => onFilterDescriptionChange(e.target.value)}
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(col3Labels)}>{labelConfigType}</span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('commonConfig.filter.configTypePlaceholder')}
                                value={filterConfigType}
                                onChange={onFilterConfigTypeChange}
                                className="w-full"
                                size="middle"
                                allowClear
                            >
                                <Option value="STRING">{t('commonConfig.configType.STRING')}</Option>
                                <Option value="JSON">{t('commonConfig.configType.JSON')}</Option>
                                <Option value="NUMBER">{t('commonConfig.configType.NUMBER')}</Option>
                                <Option value="BOOLEAN">{t('commonConfig.configType.BOOLEAN')}</Option>
                            </Select>
                        </div>
                    </div>
                </div>
                {/* 第二行 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(col1Labels)}>{labelStatus}</span>
                        <div className={inputContainerClassName}>
                            <Select
                                placeholder={t('commonConfig.filter.statusPlaceholder')}
                                value={filterIsActive}
                                onChange={onFilterIsActiveChange}
                                className="w-full"
                                size="middle"
                                allowClear
                            >
                                <Option value={true}>{t('commonConfig.table.status.active')}</Option>
                                <Option value={false}>{t('commonConfig.table.status.inactive')}</Option>
                            </Select>
                        </div>
                    </div>
                </div>
                <div className="min-w-0"></div>
                <div className="min-w-0"></div>
            </div>

            {/* 第二排：左侧操作按钮，右侧筛选和重置按钮 */}
            <div className="mt-3 mb-1 flex justify-between items-center pl-0">
                {/* 左侧操作按钮 */}
                <div className="flex gap-4">
                    <Button
                        type="primary"
                        onClick={onCreateClick}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('commonConfig.filter.createButton')}</span>
                    </Button>

                    <Button
                        type="default"
                        danger
                        onClick={onBatchDelete}
                        disabled={selectedCount === 0}
                        loading={deleteLoading}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('commonConfig.filter.batchDeleteButton')} {selectedCount > 0 && `(${selectedCount})`}</span>
                    </Button>

                    <Button
                        type="default"
                        onClick={onBatchChangeStatus}
                        disabled={selectedCount === 0}
                        loading={statusLoading}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('commonConfig.filter.batchChangeStatusButton')} {selectedCount > 0 && `(${selectedCount})`}</span>
                    </Button>

                    <Button
                        type="default"
                        onClick={onDebugConfig}
                        disabled={selectedCount === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('commonConfig.filter.debugConfigButton')}</span>
                    </Button>

                    <Button
                        type="link"
                        href="https://ccnip1tsx4n2.feishu.cn/wiki/VxAzwEJKQiVUkdkmp5ncZh66nwh"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('commonConfig.filter.userDocButton')}</span>
                    </Button>
                </div>

                {/* 右侧筛选和重置按钮 */}
                <div className="flex gap-4">
                    <Button
                        type="primary"
                        onClick={onFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('commonConfig.filter.filterButton')}</span>
                    </Button>
                    <Button
                        type="default"
                        onClick={onReset}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('commonConfig.filter.resetButton')}</span>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default CommonConfigFilter;

